import { useState } from 'react';
import { Flag, X, Loader2, AlertTriangle } from 'lucide-react';
import Button from './Button';
import Card from './Card';
import { Input } from '@/components/ui/input';
import { createReport } from '@/services/reports';
import { toast } from 'sonner';

interface ReportModalProps {
  reportedId: string;
  reportedName: string;
  onClose: () => void;
}

export function ReportModal({ reportedId, reportedName, onClose }: ReportModalProps) {
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !reason || !description) {
      toast.error('Por favor, preencha todos os campos.');
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await createReport({
        reportedId,
        title,
        reason,
        description,
      });

      if (!res.success) {
        throw new Error(res.error || 'Erro ao enviar denúncia.');
      }

      toast.success('Denúncia enviada com sucesso. Nossa equipe administrativa irá analisar.');
      onClose();
    } catch (error: any) {
      console.error('Error submitting report:', error);
      
      const errorMsg = error.response?.data?.message || error.message;
      const errorCode = error.response?.data?.error;
      
      if (errorCode === 'REPORT_ALREADY_EXISTS' || errorMsg?.includes('Você já possui uma denúncia ativa')) {
        toast.error('Você já possui uma denúncia em andamento para este usuário. Aguarde a análise.');
      } else {
        toast.error(errorMsg || 'Erro ao enviar denúncia. Tente novamente mais tarde.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-md p-6 relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Flag className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Denunciar</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Relatar problema com {reportedName}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Título da Denúncia
            </label>
            <Input
              placeholder="Ex: Comportamento inadequado"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="bg-gray-50 dark:bg-neutral-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Selecione um motivo</option>
              <option value="Falta injustificada">Falta injustificada</option>
              <option value="Conduta antiética">Conduta antiética</option>
              <option value="Problemas técnicos recorrentes">Problemas técnicos recorrentes</option>
              <option value="Assédio ou desrespeito">Assédio ou desrespeito</option>
              <option value="Outros">Outros</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrição Detalhada
            </label>
            <textarea
              placeholder="Descreva o ocorrido com o máximo de detalhes possível..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Denúncias falsas podem resultar na suspensão da <strong>sua</strong> conta. Use este recurso com responsabilidade.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="tertiary"
              className="flex-1"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1 bg-red-600 hover:bg-red-700 border-red-600 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Denúncia'
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
